import { navigate } from "gatsby";
import { appConfig } from "@src/config/appConfig";
import {
  scrollToCategoryAnchorById,
  scrollToCategoryAnchorElement,
} from "@src/components/page-content/CategoryContent/CategoryListingView/buildListingView";
import { isClientNavigable } from "./navigationRules";

const stripTrailingSlash = (path: string): string =>
  path.replace(/\/+$/, "") || "/";

/**
 * Normalise un href banner/CMS en chemin interne (`/fr/...`).
 * Strip la base Sushi Shop et les URLs absolues same-site.
 */
export const normalizeInternalPath = (
  href: string,
  baseUrl: string = appConfig.sushishopBaseUrl || ""
): string => {
  let route = (href || "").trim();
  if (!route) return "";

  if (baseUrl) {
    route = route.replace(new RegExp(`^${baseUrl}`, "i"), "");
  }

  if (/^https?:\/\//i.test(route)) {
    try {
      const url = new URL(route);
      route = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      /* keep route */
    }
  }

  if (route && !route.startsWith("/")) {
    route = `/${route}`;
  }

  return route;
};

export const findCategoryDetailsByUri = (
  uri: string
): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  const normalized = stripTrailingSlash(uri);
  const nodes = document.querySelectorAll<HTMLElement>(
    ".category-details[data-uri]"
  );
  for (const el of nodes) {
    const dataUri = stripTrailingSlash(el.dataset.uri || "");
    if (dataUri === normalized) {
      return el;
    }
  }
  return null;
};

type CategoryNavClickEvent = {
  preventDefault: () => void;
  stopPropagation: () => void;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  button?: number;
};

const isModifiedClick = (event?: CategoryNavClickEvent): boolean =>
  !!event &&
  (!!event.metaKey ||
    !!event.ctrlKey ||
    !!event.shiftKey ||
    !!event.altKey ||
    event.button === 1);

const categoryScrollNavState = {
  scrollNavigation: true,
  noScroll: true,
  isScrolling: true,
} as const;

/**
 * Navigation SPA vers une catégorie du listing (banner / CTA).
 *
 * - Si l'ancre `.category-details` est déjà dans le DOM : scroll in-page +
 *   sync URL (`scrollNavigation`) — équivalent menu latéral.
 * - Sinon : `navigate` avec `isListingPush` — même contrat que ListingPushes
 *   (CategoryLayout → handleChangeCategory).
 *
 * @returns true si le clic a été pris en charge (preventDefault appliqué).
 */
export const navigateInternalCategoryLink = (
  href: string,
  event?: CategoryNavClickEvent
): boolean => {
  if (typeof window === "undefined" || !href) return false;
  if (isModifiedClick(event)) return false;

  const route = normalizeInternalPath(href);
  if (!route || !isClientNavigable(route)) return false;

  event?.preventDefault();
  event?.stopPropagation();

  const categoryEl = findCategoryDetailsByUri(route);
  if (categoryEl) {
    const id = categoryEl.dataset.id;
    if (id) {
      scrollToCategoryAnchorById(id);
    } else {
      scrollToCategoryAnchorElement(categoryEl);
    }

    const currentPath = stripTrailingSlash(window.location.pathname);
    const targetPath = stripTrailingSlash(route.split(/[?#]/)[0] || route);
    if (currentPath !== targetPath) {
      void navigate(route, { state: { ...categoryScrollNavState } });
    }
    return true;
  }

  void navigate(route, {
    state: {
      isListingPush: true,
      ...categoryScrollNavState,
    },
  });
  return true;
};

export default navigateInternalCategoryLink;
